/* eslint-disable react/no-unstable-nested-components */
import { SupplyChainRetailerDetail, Currency, Contract } from "@api/supplyChainApi";
import { regionTypeOptions } from "@constant/ThroughShipment";
import RetailerPopup from "@page/SupplyChain/RetailerPopup";
import {
  supplyChainState,
  fetchSupplyChainRetailList,
  deleteRetailer,
  updateRetailerFilter,
} from "@redux/supplyChainSlice";

import React, { useState, useEffect } from "react";
import { PlusCircleTwoTone, ExclamationCircleOutlined, SearchOutlined, FilterTwoTone } from "@ant-design/icons";
import { Button, Table, Modal, Select, Pagination, Input, Space } from "antd";
import { SelectValue } from "antd/lib/select";
import { ColumnsType } from "antd/lib/table";
import { FilterDropdownProps } from "antd/lib/table/interface";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useAppDispatch } from "src/store";
import { FlexBox } from "src/styles/common";

import RetailerFilter from "./RetailerFilter";

const Wrapper = styled.div``;

const Text = styled.div`
  text-align: left;
`;
const Links = styled.div`
  display: flex;
  color: #1790ff;
  cursor: pointer;
`;
const Edit = styled.div`
  white-space: nowrap;
  color: #1790ff;
  cursor: pointer;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 21px;
`;
const TablePageInfo = styled.div`
  font-size: 14px;
  margin-right: 10px;
`;
const PageSelect = styled(Select)`
  margin: 0 10px;
`;
const TableContainer = styled.div`
  position: relative;
  padding: 20px 10px 50px 10px;
  border: 1px solid #f0f0f0;
`;
const TablePagination = styled(Pagination)`
  position: absolute;
  right: 21px;
  bottom: 10px;
`;

const CustomSpace = styled(Space)`
  width: 100%;

  .ant-space-item {
    width: 50%;
  }
`;

const Retailer = () => {
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const { supplyChainId } = useParams();
  const dispatch = useAppDispatch();

  const {
    supplyChainRetailList: { results: supplyChainRetailList, count },
    isFetching,
    supplyChainRetailerFilter,
  } = useSelector(supplyChainState);

  const { limit, offset } = supplyChainRetailerFilter;

  const currentPage = Math.floor(offset / limit) + 1;
  const handleOnPageSizeChange = (value: SelectValue) => {
    dispatch(
      updateRetailerFilter({
        ...supplyChainRetailerFilter,
        limit: value as number,
        offset: 0,
      }),
    );
  };

  const handleOnPageChange = (page: number) => {
    dispatch(
      updateRetailerFilter({
        ...supplyChainRetailerFilter,
        offset: (page - 1) * limit,
      }),
    );
  };

  const handleSearch = (selectedKeys: string[], confirm: (param?: any) => void, dataIndex: any) => {
    confirm();
  };
  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
      return (
        <div style={{ padding: "20px" }}>
          <Input
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <CustomSpace>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: "100%", height: 32 }}
            >
              搜尋
            </Button>
            <Button
              size="small"
              style={{ width: "100%", height: 32 }}
              onClick={() => clearFilters && handleReset(clearFilters)}
            >
              重設
            </Button>
          </CustomSpace>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined, fontSize: "15px" }} />
    ),
    onFilter: (value: any, record: any) => {
      return record[dataIndex]
        ?.toString()
        ?.toLowerCase()
        ?.includes((value as string)?.toLowerCase());
    },
  });

  const tableColumns: ColumnsType<SupplyChainRetailerDetail> = [
    {
      title: "合作通路類型",
      key: "regionType",
      dataIndex: "regionType",
      render: (data) => {
        const regionType = regionTypeOptions.find((item) => item.value === data);
        return regionType ? regionType.label : "";
      },
    },
    {
      title: "地區",
      key: "regionName",
      dataIndex: "regionName",
    },
    {
      title: "通路名稱",
      key: "retailer",
      dataIndex: "retailer",
      ...getColumnSearchProps("retailer"),
    },
    {
      title: "線別",
      key: "productLine",
      dataIndex: "productLine",
    },
    {
      title: "幣別",
      key: "currency",
      dataIndex: "currency",
      render: (data) => {
        switch (data) {
          case Currency.TWD:
            return "TWD";
          case Currency.USD:
            return "USD";
          case Currency.RMB:
            return "RMB";
          default:
            return "";
        }
      },
    },
    {
      title: "檢視合約",
      key: "contract",
      dataIndex: "contract",
      render: (data) => {
        return data ? <Links onClick={() => handleOpenContract(data)}>{data.name}</Links> : <Text>N/A</Text>;
      },
    },
    {
      title: "合約生效時間",
      key: "startAt",
      dataIndex: "startAt",
      render: (data) => {
        return data ? <Text>{data.startAt}</Text> : <Text>N/A</Text>;
      },
    },
    {
      title: "合約結束時間",
      key: "endAt",
      dataIndex: "endAt",
      render: (data) => {
        return data ? <Text>{data.endAt}</Text> : <Text>N/A</Text>;
      },
    },
    {
      title: "",
      key: "",
      dataIndex: "",
      fixed: "right",
      render: (value, data) => {
        return <Edit onClick={() => handleDelete(data.id)}>刪除</Edit>;
      },
    },
    {
      title: "",
      key: "",
      dataIndex: "",
      fixed: "right",
      render: (data) => {
        return <Edit onClick={() => handleOnOpen(data)}>供應商品列表</Edit>;
      },
    },
  ];

  const handleOpenContract = (data: Contract) => {
    if (data.path) {
      window.open(data.path, "_blank");
    }
  };
  const handleDelete = (deleteId: number) => {
    Modal.confirm({
      title: "你確定要刪除這些資料?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "是",
      cancelText: "否",
      onOk: () => {
        const filter = {
          id: deleteId,
          supplyChain: Number(supplyChainId),
        };
        dispatch(deleteRetailer(filter));
      },
    });
  };
  const handlePopup = () => {
    setOpenPopup((prev) => !prev);
  };

  const handleOnOpen = (data: SupplyChainRetailerDetail) => {
    window.open(`/operation-of-brand/supply-chain/edit/${supplyChainId}/${data.id}/${data.currency}`, "_blank");
  };
  useEffect(() => {
    const filter = {
      ...supplyChainRetailerFilter,
      supplyChain: Number(supplyChainId),
    };

    dispatch(fetchSupplyChainRetailList(filter));
  }, [dispatch, supplyChainId, supplyChainRetailerFilter]);

  return (
    <Wrapper>
      <Header>
        <FlexBox>
          <Button type="primary" icon={<PlusCircleTwoTone />} onClick={handlePopup}>
            新增通路商品列表
          </Button>
          <Button icon={<FilterTwoTone />} onClick={() => setShowFilter(!showFilter)}>
            篩選
          </Button>
          {openPopup && <RetailerPopup onClose={handlePopup} />}
        </FlexBox>
        <TablePageInfo>
          總共
          {count}
          筆, 每頁顯示
          <PageSelect defaultValue="20" onChange={(value) => handleOnPageSizeChange(value as SelectValue)}>
            <Select.Option value={20}>20</Select.Option>
            <Select.Option value={50}>50</Select.Option>
            <Select.Option value={100}>100</Select.Option>
          </PageSelect>
          筆
        </TablePageInfo>
      </Header>
      {showFilter && <RetailerFilter isVisible={showFilter} />}
      <TableContainer>
        <Table
          loading={isFetching}
          scroll={{ x: "max-content" }}
          dataSource={supplyChainRetailList}
          columns={tableColumns}
          rowKey="id"
          pagination={false}
        />
        <TablePagination
          current={currentPage}
          total={count}
          onChange={handleOnPageChange}
          showSizeChanger={false}
          pageSize={limit}
        />
      </TableContainer>
    </Wrapper>
  );
};

export default Retailer;
